import React, {useLayoutEffect, useRef} from "react";
import {Group, Layer, Rect} from "react-konva";
import Konva from "konva";
import Position from "@/util/Position.ts";
import {useStage} from "@/components/konva/stage/ResponsiveStageHooks.ts";
import {useSelectionRectangle} from "@/tabletop/selection/SelectionRectangleHooks.ts";
import SelectionView from "@/tabletop/selection/SelectionView.tsx";
import {useTableTopSelection} from "@/tabletop/selection/TableTopSelectionHooks.ts";
import {useEncounterViewPort} from "@/encounter/viewport/EncounterViewPortHooks.ts";
import Size from "@/util/Size.ts";
import {zoomMapTransform} from "./MapZoom.ts";

export default function GameMasterPannableLayer({children, placingAnnotation = false, onMapClick,
    onMapPointerDown}: {
    children: React.ReactNode;
    placingAnnotation?: boolean;
    onMapClick?: (position: Position) => void;
    onMapPointerDown?: () => void;
}) {
    const pan = useRef<{origin: Position; moved: boolean} | undefined>(undefined);
    const {
        mapTransform,
        updateMapTransform,
        resizeStage,
        persistCurrentViewPort,
        playerViewPort,
        playerViewPortLocked,
    } = useEncounterViewPort();
    const {clearSelection} = useTableTopSelection();
    const {handleStartSelection, handleMoveSelection, handleEndSelection} = useSelectionRectangle();
    const {stageSize, stageMeasured} = useStage();
    const layerRef = useRef<Konva.Layer>(null);
    const handledStageSize = useRef<Size | undefined>(undefined);

    useLayoutEffect(() => {
        if (!stageMeasured) return;
        const previousSize = handledStageSize.current;
        if (previousSize?.width === stageSize.width && previousSize.height === stageSize.height) return;

        handledStageSize.current = stageSize;
        resizeStage(stageSize);
    }, [resizeStage, stageMeasured, stageSize]);

    const handleWheel = ({evt}: Konva.KonvaEventObject<WheelEvent>) => {
        evt.preventDefault();
        const layer = layerRef.current;
        const pointer = layer?.getStage()?.getPointerPosition();
        if (!pointer) return;

        const nextTransform = zoomMapTransform(mapTransform, pointer, evt.deltaY < 0);
        if (nextTransform !== mapTransform) updateMapTransform(nextTransform);
    };

    const handleMouseDown = (event: Konva.KonvaEventObject<MouseEvent>) => {
        if (event.evt.button === 0) onMapPointerDown?.();
        if (event.evt.button === 0 && event.target.hasName("LayerClickTarget") && !placingAnnotation) {
            clearSelection();
            handleStartSelection(event);
        }
        if (event.evt.button === 1) {
            pan.current = {
                origin: {
                    x: mapTransform.x - event.evt.clientX,
                    y: mapTransform.y - event.evt.clientY,
                },
                moved: false,
            };
        }
    };

    const handleMouseMove = (event: Konva.KonvaEventObject<MouseEvent>) => {
        handleMoveSelection(event);
        if (event.evt.buttons === 4 && pan.current) {
            const x = pan.current.origin.x + event.evt.clientX;
            const y = pan.current.origin.y + event.evt.clientY;
            if (x === mapTransform.x && y === mapTransform.y) return;
            pan.current.moved = true;
            updateMapTransform({
                ...mapTransform,
                x,
                y,
            });
        }
    };

    const handleMouseUp = (event: Konva.KonvaEventObject<MouseEvent>) => {
        handleEndSelection();
        if (event.evt.button !== 1) return;
        const moved = pan.current?.moved;
        pan.current = undefined;
        if (moved) persistCurrentViewPort();
    };

    const handleClick = (event: Konva.KonvaEventObject<MouseEvent>) => {
        if (!placingAnnotation || !event.target.hasName("LayerClickTarget")) return;
        const pointer = event.target.getStage()?.getPointerPosition();
        if (!pointer) return;
        clearSelection();
        onMapClick?.({
            x: (pointer.x - mapTransform.x) / mapTransform.scale,
            y: (pointer.y - mapTransform.y) / mapTransform.scale,
        });
    };

    return <Layer
        ref={layerRef}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onClick={handleClick}
        onContextMenu={({evt}) => evt.preventDefault()}
    >
        <Rect
            width={stageSize.width}
            height={stageSize.height}
            fill="#222"
            name="LayerClickTarget"
        />
        <Group
            x={mapTransform.x}
            y={mapTransform.y}
            scaleX={mapTransform.scale}
            scaleY={mapTransform.scale}
        >
            {children}
            {playerViewPortLocked && <Rect
                x={playerViewPort.centerX - playerViewPort.visibleWidth / 2}
                y={playerViewPort.centerY - playerViewPort.visibleHeight / 2}
                width={playerViewPort.visibleWidth}
                height={playerViewPort.visibleHeight}
                stroke="#000"
                strokeWidth={4 / mapTransform.scale}
                dash={[10 / mapTransform.scale, 10 / mapTransform.scale]}
                listening={false}
            />}
        </Group>
        <SelectionView/>
    </Layer>;
}
